<template>
  <div class="w-full mx-auto flex flex-col gap-5 h-full">
    <!-- Search & Filter -->
    <div
      v-if="props.selectOptions || showSearchInput"
      class="p-4 rounded-lg bg-secondary-50 border border-secondary-100 shadow-sm"
    >
      <div
        class="flex md:flex-row md:flex-wrap flex-col items-center md:justify-between h-auto justify-center gap-4"
      >
        <VInput
          v-if="showSearchInput"
          v-model="search"
          class="md:w-96 w-full max-w-xs"
          type="text"
          :placeholder="translate(props.searchPlaceholder, props.pageLang)"
          :schema="props.searchSchema"
          :pageLang="props.pageLang"
          :pageDir="props.pageDir"
          @keyup.enter="emit('handleSearch', { currentPage: 1, search })"
        />
        <VSelect
          v-if="props.selectOptions"
          v-model="selectedFilterValue"
          class="md:w-72 w-full max-w-xs"
          :options="props.selectOptions.options"
          :pageLang="props.pageLang"
          :pageDir="props.pageDir"
          :placeholder="translate(props.selectPlaceholder, props.pageLang)"
        />
        <slot name="filters"></slot>
      </div>
    </div>

    <!-- Table -->
    <div
      class="overflow-x-auto border border-secondary-100 rounded-lg shadow-sm min-h-[60vh]"
    >
      <table class="min-w-full divide-y divide-gray-200 text-sm">
        <thead class="bg-secondary-50 text-gray-700 uppercase tracking-wider">
          <tr class="text-left">
            <th v-if="selectable" class="py-3 px-6">
              <input
                type="checkbox"
                :checked="isAllSelected"
                class="rounded border-gray-300"
                @change="toggleSelectAll"
              />
            </th>
            <th
              v-for="col in props.cols"
              :key="col.field"
              class="py-3 px-6 cursor-pointer font-semibold"
            >
              <div class="flex items-center gap-1">
                <span>{{ col.title }}</span>
                <div
                  v-if="
                    props.showsortDirection &&
                    sortableColumns?.includes(col.field)
                  "
                  class="flex flex-col items-center justify-center"
                >
                  <chevron-up
                    :size="16"
                    :stroke-width="2.5"
                    :absolute-stroke-width="true"
                    class="text-secondary-500 hover:text-secondary-200"
                    @click="handleSort(col.field, 'asc')"
                  ></chevron-up>
                  <chevron-down
                    :size="16"
                    :stroke-width="2.5"
                    :absolute-stroke-width="true"
                    class="text-secondary-500 hover:text-secondary-200 mt-[-6px]"
                    @click="handleSort(col.field, 'desc')"
                  ></chevron-down>
                </div>
              </div>
            </th>
          </tr>
        </thead>

        <tbody class="divide-y divide-gray-200 bg-white">
          <!-- Loading -->
          <tr v-if="props.loading">
            <td
              :colspan="props.cols.length + (selectable ? 1 : 0)"
              class="px-6 py-4"
            >
              <div class="animate-pulse space-y-4">
                <div class="h-4 bg-gray-200 rounded w-full"></div>
                <div class="h-4 bg-gray-200 rounded w-5/6"></div>
                <div class="h-4 bg-gray-200 rounded w-4/6"></div>
                <div class="h-4 bg-gray-200 rounded w-full"></div>
                <div class="h-4 bg-gray-200 rounded w-5/6"></div>
                <div class="h-4 bg-gray-200 rounded w-4/6"></div>
                <div class="h-4 bg-gray-200 rounded w-full"></div>
              </div>
            </td>
          </tr>

          <tr v-else-if="props.rows.length === 0">
            <td
              :colspan="props.cols.length + (selectable ? 1 : 0)"
              class="px-6 py-8 sm:text-md text-sm font-medium text-center text-gray-400"
            >
              {{ translate('vtable.no_data', props.pageLang) }}
            </td>
          </tr>

          <tr
            v-for="(row, index) in props.rows"
            v-else
            :key="index"
            class="hover:bg-gray-50"
          >
            <td v-if="selectable" class="px-6 py-4">
              <input
                v-model="selectedRows"
                type="checkbox"
                :value="row"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                @change="emit('row-selected', selectedRows)"
              />
            </td>
            <td
              v-for="col in props.cols"
              :key="col.field"
              class="px-6 py-4 whitespace-nowrap"
            >
              <slot :name="col.field" :value="row">{{ row[col.field] }}</slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <VPagination
      :pageLang="props.pageLang"
      :pageDir="props.pageDir"
      :totalRows="props.rowsCount"
      :currentPage="currentPage"
      :itemsPerPage="props.itemsPerPage"
      :rows="props.rows"
      @change-page="changePage"
    />
  </div>
</template>

<script lang="ts" setup>
// Import vue files
import { ref, computed, watch } from 'vue'
import { ChevronDown, ChevronUp } from 'lucide-vue-next'

// Import Vue Custom Components
import { translate } from 'src/utils/translation'
import { ValidatorSchema } from 'src/utils/Validators'

// Props and Emits
const props = defineProps<{
  cols: { field: string; title: string }[]
  rows: any[]
  rowsCount: number
  loading: boolean
  selectOptions?: any
  showSearchInput?: boolean
  selectable?: boolean
  showsortDirection?: boolean
  selectPlaceholder?: string | any
  sortableColumns?: string[]
  searchPlaceholder?: string | any
  searchSchema?: ValidatorSchema
  itemsPerPage?: number
  pageLang?: string
  pageDir?: string
}>()

const emit = defineEmits([
  'changePage',
  'row-selected',
  'handleFilter',
  'handleSort',
  'handleSearch',
])

// Variables
const currentPage = ref(1)
const selectedFilterValue = ref<any>(null)
const selectedRows = ref<any[]>([])
const search = ref('')

// Functions
const changePage = (page: number) => {
  currentPage.value = page
  emit('changePage', { currentPage: page, search: search.value })
}

const isAllSelected = computed(() => false)

const toggleSelectAll = () => {
  return
}

const handleSort = (key: string, sortDirection: string) => {
  emit('handleSort', { key, direction: sortDirection })
}

// Watchers
watch(
  () => selectedFilterValue.value,
  newValue => {
    if (newValue) {
      emit('handleFilter', { currentPage: 1, type: newValue })
    }
  },
)
watch(
  () => search.value,
  newValue => {
    if (newValue) {
      emit('handleSearch', { currentPage: 1, search: newValue })
    }
  },
)
</script>
